// Nora Concierge — config and intent rules for the regelbasierte Assistenz.
// Plain JS objects. No external LLM. Premium copy, never cheesy.

export const NORA_GREETING =
  "Willkommen bei Livinora. Ich bin Nora — Ihre Projektassistenz für Konzepte, Raumsets, Refit und Termine. Wobei darf ich helfen?";

export const QUICK_ACTIONS = [
  { id: "project", label: "Projekt konfigurieren", flow: "project" },
  { id: "shop", label: "Shop-Raumsets ansehen", flow: "shop_set" },
  { id: "appointment", label: "Termin anfragen", flow: "appointment" },
  { id: "callback", label: "Rückrufbitte", flow: "callback" },
  { id: "brochure", label: "Broschüre anfordern", flow: "brochure" },
  { id: "explain_refit", label: "Refit erklären", flow: "explain", topic: "refit" },
  { id: "explain_diff", label: "Unterschied Shop / Konfigurator", flow: "explain", topic: "diff" },
  { id: "explain_package", label: "Passendes Paket finden", flow: "explain", topic: "package" },
];

export const FALLBACK_ACTIONS = [
  { id: "project", label: "Komplettes Projekt", flow: "project" },
  { id: "shop_set", label: "Raumset", flow: "shop_set" },
  { id: "appointment", label: "Termin", flow: "appointment" },
  { id: "callback", label: "Rückruf", flow: "callback" },
];

// ---- Intent detection -----------------------------------------------------
// Kept very explicit for traceability. Order matters — earlier wins.
const INTENTS = [
  { intent: "appointment_request", patterns: [/termin/i, /beratungstermin/i, /vereinbar/i, /berat(en|ung)/i] },
  { intent: "callback_request", patterns: [/rückruf/i, /ruckruf/i, /rufen sie mich/i, /anruf/i, /kontaktier/i] },
  { intent: "brochure_request", patterns: [/broschüre/i, /broschure/i, /unterlagen/i, /datenblatt/i, /\bpdf\b/i] },
  { intent: "shop_set_request", patterns: [/raumset/i, /raum-set/i, /shop/i, /badmöbel/i, /bad-set/i, /schlafzimmer.{0,8}set/i, /wohnzimmer.{0,8}set/i, /nachbestell/i] },
  { intent: "project_request", patterns: [/projekt/i, /einrichten/i, /apartment/i, /ferienhaus/i, /ferienwohnung/i, /komplette ausstattung/i, /konzept/i, /konfigurator/i] },
  { intent: "explain_refit", patterns: [/refit/i, /modernisier/i, /aufgefrisch/i] },
  { intent: "explain_packages", patterns: [/paket/i, /basis/i, /komfort/i, /premium-paket/i] },
  { intent: "explain_style_series", patterns: [/stilserie/i, /küste/i, /alpin/i, /skandi/i, /landhaus/i, /urban retreat/i] },
  { intent: "explain_materials", patterns: [/material/i, /holz/i, /\bfsc\b/i, /verarbeit/i, /herkunft/i] },
  { intent: "price_orientation", patterns: [/preis/i, /kost/i, /richtpreis/i, /budget/i] },
  { intent: "explain_shop", patterns: [/wie funktioniert.+shop/i, /was ist.+shop/i] },
  { intent: "explain_configurator", patterns: [/wie funktioniert.+konfigurator/i, /was ist.+konfigurator/i] },
  { intent: "explain_livinora", patterns: [/was ist livinora/i, /wer seid ihr/i, /über livinora/i, /was bietet/i] },
  { intent: "greeting", patterns: [/^hallo$/i, /^hi$/i, /^guten /i, /^hey$/i] },
];

export function detectIntent(text) {
  const t = (text || "").trim();
  if (!t) return "fallback";
  for (const row of INTENTS) {
    if (row.patterns.some((p) => p.test(t))) return row.intent;
  }
  return "fallback";
}

// ---- Knowledge answers ----------------------------------------------------
export const ANSWERS = {
  greeting:
    "Schön, dass Sie da sind. Ich kann Ihnen helfen, den passenden nächsten Schritt zu finden.",
  explain_livinora:
    "Livinora liefert schlüsselfertige Einrichtungskonzepte für Ferienhäuser, Ferienwohnungen und kleinere Betreiberobjekte. Sie können ein komplettes Projekt konfigurieren oder standardisierte Raumsets im Shop anfragen.",
  explain_shop:
    "Der Shop ist für standardisierte Raumsets gedacht — keine Bestellung, sondern eine Anfrage. So können Mengen, Lieferung und Montage sauber geprüft werden.",
  explain_configurator:
    "Der Konfigurator führt durch komplette Projekte: Objekttyp, Stilserie und Paket — am Ende steht eine strukturierte Projektanfrage.",
  explain_refit:
    "Refit bezeichnet die gezielte Modernisierung bestehender Objekte — von einzelnen Bereichen bis zur umfassenden Auffrischung.",
  explain_packages:
    "Drei Pakete strukturieren das Projekt: Basis, Komfort und Premium — von essenzieller Ausstattung bis zum vollständig kuratierten Konzept.",
  explain_style_series:
    "Fünf Stilserien stehen zur Auswahl: Küste, Alpin, Skandi, Landhaus Modern und Urban Retreat.",
  explain_materials:
    "Korpus, Türen und Schubladen aus FSC-zertifiziertem Holzwerkstoff. Beschläge und Auszüge in Metall, Füße aus Massivholz, Griffe aus Holz. Holz aus Deutschland.",
  price_orientation:
    "Als Orientierung: Flur ca. 1.690–2.890 €, Essbereich 2.890–5.190 €, Schlafzimmer 4.590–7.490 €, Wohnzimmer 5.190–8.590 €, Apartment-Set 9.790–14.890 €, Refit-Set 6.790–12.490 €, Badmöbel 1.990–3.990 €. Lieferung, Montage und finale Preise individuell.",
};

export const FALLBACK_TEXT =
  "Geht es um ein komplettes Projekt, ein Raumset, einen Termin oder einen Rückruf?";

// ---- Flow option lists ----------------------------------------------------
export const APPOINTMENT_TOPICS = [
  "Neues Objekt",
  "Refit / Modernisierung",
  "Shop-Raumset",
  "Gewerbliche Anfrage",
  "Allgemeine Beratung",
];
export const APPOINTMENT_TIMES = ["09:30", "11:00", "14:00", "16:30"];

export const CALLBACK_TIMES = [
  "Heute",
  "Morgen",
  "Diese Woche",
  "Vormittags",
  "Nachmittags",
];

export const BROCHURE_TYPES = [
  "Livinora Gesamtbroschüre",
  "Stilserien-Überblick",
  "Refit-Informationen",
  "Shop-Raumsets",
  "Gewerbliche Ausstattung",
];

export const PROJECT_OBJECT_TYPES = [
  "Ferienwohnung",
  "Ferienhaus",
  "Apartment",
  "Serviced Apartment",
  "Kleines Hotel",
  "Betreiberobjekt",
  "Anderes Objekt",
];
export const PROJECT_SCOPES = [
  "Neueinrichtung",
  "Teil-Refit",
  "Komplette Modernisierung",
  "Nachbestellung",
];
export const PROJECT_STYLES = [
  "Küste",
  "Alpin",
  "Skandi",
  "Landhaus Modern",
  "Urban Retreat",
  "Noch offen",
];
export const PROJECT_PACKAGES = ["Basis", "Komfort", "Premium", "Noch offen"];

export const SHOP_USAGE_TYPES = ["Privat", "Gewerblich"];
